import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import usePagination from '../hooks/usePagination';
import useSearch from '../hooks/useSearch';
import useOptimizationType from '../hooks/useOptimizationType';
import OptimizationTypeSelector from './shared/OptimizationTypeSelector';
import PaginationFooter from './shared/PaginationFooter';
import EmptyState from './shared/EmptyState';
import CreditsWidget from './shared/CreditsWidget';
import { showErrorToast, showSuccessToast } from '../utils/toast';

function ProductSelection({ onJobStarted, onJobComplete, hideCredits }) {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAllStore, setSelectAllStore] = useState(false); // Store-wide select all flag
  const [loading, setLoading] = useState(false); // For optimization action button
  const [fetching, setFetching] = useState(false); // For products data fetch
  const [searching, setSearching] = useState(false); // For products search fetch
  const [error, setError] = useState(null);
  const [pageSize] = useState(50);
  
  // Use custom hooks
  const { optimizationType, handleOptimizationTypeChange } = useOptimizationType('regular');
  const { currentPage, pageInfo, updatePageInfo, goToNextPage, goToPreviousPage, resetToFirstPage } = usePagination({ pageSize });
  const { searchQuery, debouncedSearchQuery, handleSearchChange, clearSearch } = useSearch(500, () => {
    // Reset to page 1 when search changes (debounced)
    resetToFirstPage();
  });
  
  // Use ref to store authenticatedFetch to avoid recreating callback dependencies
  const authenticatedFetchRef = useRef(authenticatedFetch);
  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  // Fetch products function - wrapped in useCallback to ensure stable reference
  const fetchProducts = useCallback(async (page = 1, search = '') => {
    setFetching(true);
    setSearching(!!search); // Set searching true only if there's a search query
    setError(null);

    try {
      const response = await authenticatedFetchRef.current('/api/products', {
        method: 'POST',
        body: JSON.stringify({
          page,
          pageSize,
          search: search || undefined
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to fetch products: ${response.status}`);
      }

      setProducts(data.products || []);
      if (data.pageInfo) {
        updatePageInfo(data.pageInfo);
      }
      
      // Only clear page-level selection when navigating pages, not when search changes
      // And not when selectAllStore is active
      if (!selectAllStore) {
        setSelectedProducts(new Set());
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to fetch products');
    } finally {
      setFetching(false);
      setSearching(false);
    }
  }, [pageSize, updatePageInfo, selectAllStore]); // Dependencies: pageSize, updatePageInfo (from hook), selectAllStore

  // Effect to fetch products when page or debounced search changes
  useEffect(() => {
    fetchProducts(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery, fetchProducts]); // Dependencies: currentPage, debouncedSearchQuery, fetchProducts

  // Select all on CURRENT PAGE
  const handleSelectAll = useCallback(() => {
    if (selectAllStore) {
      setSelectAllStore(false);
      const pageIds = new Set(products.map((p) => p.id));
      setSelectedProducts(pageIds);
      return;
    }
    
    const pageIds = new Set(products.map((p) => p.id));
    const allPageSelected = selectedProducts.size === products.length && products.length > 0;
    
    if (allPageSelected) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(pageIds);
    }
  }, [selectedProducts.size, products, selectAllStore]);

  // Select ALL ITEMS IN STORE
  const handleSelectAllStore = useCallback(() => {
    setSelectAllStore(true);
    if (searchQuery.trim() !== '') {
      clearSearch();
    }
    setSelectedProducts(new Set());
  }, [searchQuery, clearSearch]);

  const handleUnselectAll = useCallback(() => {
    setSelectAllStore(false);
    setSelectedProducts(new Set());
  }, []);

  const handleUndoSelectAllStore = useCallback(() => {
    setSelectAllStore(false);
    const pageIds = new Set(products.map((p) => p.id));
    setSelectedProducts(pageIds);
  }, [products]);

  const handleProductToggle = useCallback((productId) => {
    if (selectAllStore) {
      return;
    }
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  }, [selectedProducts, selectAllStore]);

  const handleStartOptimization = useCallback(async () => {
    if (!selectAllStore && selectedProducts.size === 0) {
      setError('Please select at least one product or enable "Select all products from store"');
      return;
    }

    if (!optimizationType) {
      setError('Please select an optimization type.');
      return;
    }
  
    setLoading(true);
    setError(null);

    try {
      // Pre-check: verify user has at least 1 credit before even calling the optimize API
      const balRes  = await authenticatedFetchRef.current('/api/credits/balance');
      const balData = await balRes.json();
      let remaining = 0;
      if (balData.success) {
        remaining = balData.data?.remainingCredits ?? 0;
        if (remaining <= 0) {
          showErrorToast('You have 0 credits remaining. Please purchase more credits before optimizing.');
          setLoading(false);
          return;
        }
      }

      // Check if selected images exceed remaining credits (only if not selectAllStore)
      if (!selectAllStore) {
        let totalSelectedImages = 0;
        products.forEach(p => {
          if (selectedProducts.has(p.id)) {
            totalSelectedImages += (p.remainingImagesCount ?? p.images?.length ?? 0);
          }
        });

        if (totalSelectedImages > remaining) {
          showErrorToast(remaining === 0
            ? `No credits remaining. Please purchase more credits to continue.`
            : `Credit limit reached. You selected ${totalSelectedImages} images but only have ${remaining} credits. Please select fewer images or purchase more credits.`
          );
          setLoading(false);
          return;
        }
      }

      const payload = {
        selectAll: selectAllStore,
        optimizationType: optimizationType,
      };

      if (selectAllStore) {
        if (debouncedSearchQuery.trim()) {
          payload.filters = {
            search: debouncedSearchQuery.trim()
          };
        }
      } else {
        payload.productIds = Array.from(selectedProducts);
      }

      const response = await authenticatedFetchRef.current('/api/optimize', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 402 && data.errorCode === 'INSUFFICIENT_CREDITS') {
          showErrorToast(data.message || 'No credits remaining. Purchase more credits to continue optimizing.');
        } else if (data.code === 'MAX_JOBS_RUNNING') {
          showErrorToast(data.message || 'Maximum 2 optimization jobs can run simultaneously. Please wait for one to complete.');
        } else {
          throw new Error(data.message || 'Failed to start optimization');
        }
        setLoading(false); // Only set loading to false on error
        return;
      }

      showSuccessToast('Optimization job started successfully');
      navigate('/', { state: { pendingJobId: data.jobId || null, wantScrollToJobs: true } });

    } catch (err) {
      setError(err.message || 'Failed to start optimization');
    } finally {
      setLoading(false);
    }
  }, [selectAllStore, selectedProducts, optimizationType, onJobStarted, onJobComplete, debouncedSearchQuery, fetchProducts, currentPage]); // Added fetchProducts and currentPage as dependencies

  const allSelected = products.length > 0 && (selectAllStore || selectedProducts.size === products.length);
  const someSelected = !selectAllStore && selectedProducts.size > 0 && selectedProducts.size < products.length;
  const totalProductsCount = pageInfo.totalProducts;

  return (
    <>
      <s-section padding="none">
        <s-stack gap="small-200">
          {error && (
            <s-banner tone="critical" dismissible onDismiss={() => setError(null)}>
              <s-text>{error}</s-text>
            </s-banner>
          )}

          {/* Sticky header */}
          <div className="sticky-header">
            <s-stack paddingBlockStart="base" gap="small-200">
              {!hideCredits && (
                <s-stack direction="inline" justifyContent="end">
                  <CreditsWidget onPurchase={() => navigate('/pricing')} />
                </s-stack>
              )}

              <s-grid gridTemplateColumns="1fr auto auto auto" gap="base" alignItems="center">
                <s-search-field
                  label="Search"
                  labelAccessibilityVisibility="exclusive"
                  placeholder={searchQuery.trim() ? 'Search...' : 'Filter products'}
                  value={searchQuery}
                  onInput={(e) => handleSearchChange(e)}
                  disabled={selectAllStore}
                />
                <s-button onClick={() => { resetToFirstPage(); fetchProducts(1, debouncedSearchQuery); }} disabled={fetching} variant="secondary">
                  Refresh
                </s-button>
                <OptimizationTypeSelector
                  optimizationType={optimizationType}
                  onChange={handleOptimizationTypeChange}
                  disabled={fetching || searching || selectAllStore}
                />
                <s-button
                  variant="primary"
                  onClick={handleStartOptimization}
                  loading={loading}
                  disabled={loading || (!selectAllStore && selectedProducts.size === 0)}
                >
                  Start Optimization {selectAllStore ? '(All)' : `(${selectedProducts.size})`}
                </s-button>
              </s-grid>

              {/* Shimmer loaders */}
              {(fetching || searching) && (
                <s-box border="base" borderRadius="base">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <s-box key={i} borderStyle="none none solid none" border="base" padding="base">
                      <s-grid gridTemplateColumns="auto auto 1fr auto" gap="base" alignItems="center">
                        <div className="shimmer-loader" style={{ height: 24, width: 24 }} />
                        <div className="shimmer-loader" style={{ height: 20, width: 20 }} />
                        <s-stack gap="small">
                          <div className="shimmer-loader" style={{ height: 18, width: 200 }} />
                          <div className="shimmer-loader" style={{ height: 14, width: 150 }} />
                        </s-stack>
                        <div className="shimmer-loader" style={{ height: 32, width: 120 }} />
                      </s-grid>
                    </s-box>
                  ))}
                </s-box>
              )}

              {/* Select-all row */}
              {!fetching && !searching && products.length > 0 && (
                <s-stack gap="small-200">
                  <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                      <s-checkbox
                        label={`Showing ${products.length} ${products.length === 1 ? 'product' : 'products'}`}
                        checked={allSelected || selectAllStore}
                        indeterminate={someSelected && !selectAllStore}
                        onInput={handleSelectAll}
                        disabled={selectAllStore}
                      />
                      {!selectAllStore && (selectedProducts.size > 0 || allSelected) && debouncedSearchQuery.trim() === '' && (
                        <>
                          <s-button variant="secondary" size="small" commandFor="ps-select-all-menu">
                            <s-text size="small">{selectedProducts.size || products.length} selected</s-text>
                          </s-button>
                          <s-menu id="ps-select-all-menu" accessibilityLabel="Selection actions">
                            <s-button onClick={handleSelectAllStore}>Select all {totalProductsCount} in this store</s-button>
                            <s-button onClick={handleUnselectAll}>Unselect all</s-button>
                          </s-menu>
                        </>
                      )}
                      {selectAllStore && (
                        <>
                          <s-button variant="secondary" size="small" commandFor="ps-undo-select-all-menu">
                            <s-text size="small">All {totalProductsCount} in this store selected</s-text>
                          </s-button>
                          <s-menu id="ps-undo-select-all-menu" accessibilityLabel="Selection actions">
                            <s-button onClick={handleUndoSelectAllStore}>Undo select all</s-button>
                            <s-button onClick={handleUnselectAll}>Unselect all</s-button>
                          </s-menu>
                        </>
                      )}
                    </s-stack>
                  </s-grid>
                </s-stack>
              )}
            </s-stack>
          </div>

          {/* Product list — flat (no accordion) */}
          {!fetching && !searching && products.length > 0 && (
            <div className="image-management-table">
              <div className="ps-product-list-header">
                <s-text type="strong">Product</s-text>
                <s-text type="strong">Images Remaining to Optimize</s-text>
              </div>

              <div>
                {products.map((item) => {
                  const remaining = item.remainingImagesCount ?? item.images?.length ?? 0;
                  return (
                    <div key={item.id} className="image-management-product-row">
                      <div 
                        className="ps-product-list-row clickable-row"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          if (e.target.closest('s-checkbox,input')) return;
                          handleProductToggle(item.id);
                        }}
                      >
                        <s-checkbox
                          checked={selectedProducts.has(item.id) || selectAllStore}
                          disabled={selectAllStore}
                          onInput={(e) => { e.stopPropagation(); handleProductToggle(item.id); }}
                        />
                        {item.featuredImageUrl ? (
                          <s-box border="base" borderRadius="base" overflow="hidden" inlineSize="40px" blockSize="40px" className="product-thumbnail-container">
                            <img src={item.featuredImageUrl} alt={item.title} className="product-thumbnail-img" />
                          </s-box>
                        ) : (
                          <s-box inlineSize="40px" blockSize="40px" borderRadius="base" className="product-thumbnail-container">
                            <s-text tone="subdued" className="product-thumbnail-fallback">📦</s-text>
                          </s-box>
                        )}
                        <s-text emphasis="strong" className="ps-product-title">{item.title}</s-text>
                        <s-text tone="subdued" className="ps-remaining-count">
                          {remaining} {remaining === 1 ? 'image' : 'images'}
                        </s-text>
                      </div>
                    </div>
                  );
                })}
              </div>

              <PaginationFooter pageInfo={pageInfo} onPrevious={goToPreviousPage} onNext={goToNextPage} loading={fetching} itemName="products" />
            </div>
          )}

          {/* Empty state */}
          {!fetching && !searching && products.length === 0 && (
            debouncedSearchQuery.trim() ? (
              <EmptyState
                icon="🔍"
                heading="No products found"
                message={`No products match "${debouncedSearchQuery.trim()}". Try a different search term.`}
                actionContent="Clear Search"
                onAction={clearSearch}
              />
            ) : (
              <EmptyState
                icon="🎉"
                heading="All products are optimized!"
                message="Every product image in your store has already been optimized. Check the Optimized tab to review savings or re-optimize as needed."
              />
            )
          )}
        </s-stack>
      </s-section>
    </>
  );
}

export default ProductSelection;
